/**
 * FlowNode — custom React Flow node for flow visualization.
 *
 * Displays: event name, session count, percentage, timing, optional KPI badges.
 * Supports selected/default states and configurable accent color.
 */
import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Clock, TrendingUp, Globe } from 'lucide-react';
import { cn } from '@/lib/cn';
import { NODE_WIDTH, SESSION_COLORS } from './flow.constants';

export interface FlowNodePayload {
  event: string;
  count: number;
  /** Percentage relative to parent node */
  relativePct: number;
  /** Percentage relative to first node in the path */
  absolutePct: number;
  kpiPct: number | null;
  kpiCount: number | null;
  crossSessionKpiPct: number | null;
  crossSessionKpiCount: number | null;
  isSelected: boolean;
  isTerminal: boolean;
  level: number;
  accentColor: string;
  startingCount: number | null;
  sessionIndex: number;
  canContinue: boolean;
  isContinuationLoading: boolean;
  avgTimeSec: number | null;
  cumulativeTimeSec: number | null;
  isFirstNode: boolean;
  isContinuationStart: boolean;
}

export function formatDuration(sec: number): string {
  if (sec < 1) return '< 1s';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

type FlowNodeProps = NodeProps & { data: FlowNodePayload };

function FlowNodeComponent({ data }: FlowNodeProps) {
  const {
    event,
    count,
    relativePct,
    absolutePct,
    kpiPct,
    kpiCount,
    crossSessionKpiPct,
    crossSessionKpiCount,
    isSelected,
    isTerminal,
    level,
    accentColor,
    sessionIndex,
    canContinue,
    isContinuationLoading,
    avgTimeSec,
    cumulativeTimeSec,
    isFirstNode,
    isContinuationStart,
  } = data;

  const formattedCount = count.toLocaleString();
  const relPctValue = relativePct * 100;
  const absPctValue = absolutePct * 100;
  const formattedRelPct = `${relPctValue.toFixed(1)}%`;
  const formattedAbsPct = `${absPctValue.toFixed(1)}%`;
  const showBothPcts = level > 0;

  const sessionColor = SESSION_COLORS[sessionIndex % SESSION_COLORS.length];
  const isSessionTinted = sessionIndex > 0;

  const hasStepTime = avgTimeSec != null && avgTimeSec > 0;
  const hasCumulativeTime = cumulativeTimeSec != null && cumulativeTimeSec > 0;
  const showTime = !isFirstNode && (isTerminal ? (hasStepTime || hasCumulativeTime) : true);

  const hasAnyKpi = kpiPct != null || crossSessionKpiPct != null;

  const barWidth = Math.min(Math.max(relPctValue, 0), 100);

  return (
    <div
      className={cn(
        'rounded-xl border-2 px-4 py-3 cursor-pointer transition-all duration-150 relative',
        'hover:shadow-lg hover:scale-[1.02]',
        'flex flex-col gap-1.5',
        isTerminal && 'border-dashed',
        isTerminal && !isSelected && !isSessionTinted && 'bg-slate-50 border-slate-300 text-slate-500',
        isTerminal && isSelected && !isSessionTinted && 'bg-slate-200 border-slate-400 text-slate-700',
        !isTerminal && !isSelected && !isSessionTinted && 'bg-white border-slate-200 text-slate-900 hover:border-slate-300 shadow-sm',
        !isTerminal && isSelected && 'text-white shadow-xl scale-[1.02]',
        !isTerminal && !isSelected && isSessionTinted && `${sessionColor.bg} ${sessionColor.border} text-slate-900 shadow-sm`,
        isTerminal && !isSelected && isSessionTinted && `${sessionColor.bg} ${sessionColor.border} text-slate-500`,
        isTerminal && isSelected && isSessionTinted && `${sessionColor.bg} ${sessionColor.border} text-slate-700`,
      )}
      style={{
        width: NODE_WIDTH,
        ...(isSelected && !isTerminal
          ? { backgroundColor: accentColor, borderColor: accentColor }
          : {}),
      }}
    >
      {/* Handles */}
      {level > 0 && (
        <Handle
          type="target"
          position={Position.Left}
          className="!bg-slate-400 !w-2.5 !h-2.5 !border-0 !-left-[5px]"
        />
      )}
      <Handle
        type="source"
        position={Position.Right}
        className="!bg-slate-400 !w-2.5 !h-2.5 !border-0 !-right-[5px]"
      />

      {/* Session badge */}
      {isSessionTinted && (
        <span
          className="absolute -top-2.5 -right-2.5 text-[10px] font-bold px-2 py-0.5 rounded-full text-white shadow"
          style={{ backgroundColor: sessionColor.accent ?? '#94a3b8' }}
        >
          S{sessionIndex + 1}
        </span>
      )}

      {/* ── Event name ────────────────────────────────────────────── */}
      <div
        className={cn(
          'font-semibold truncate leading-snug',
          isTerminal ? 'text-sm italic' : 'text-[15px]',
        )}
        title={event}
      >
        {isTerminal && canContinue ? (
          <span className="flex items-center gap-1.5">
            {isContinuationLoading ? (
              <>
                <span className="inline-block w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading next session…</span>
              </>
            ) : (
              <>
                <span>(none)</span>
                <span className="text-xs text-blue-500 font-medium">&rarr; continue</span>
              </>
            )}
          </span>
        ) : (
          event
        )}
      </div>

      {/* ── Count + Percentages ───────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'text-lg font-bold tabular-nums leading-none',
            isSelected && !isTerminal ? 'text-white' : isTerminal ? 'text-slate-500' : 'text-slate-800',
          )}
        >
          {formattedCount}
        </span>
        {showBothPcts ? (
          <span className="flex items-center gap-1">
            <span
              className={cn(
                'text-sm font-semibold tabular-nums',
                isSelected && !isTerminal ? 'text-white/70' : 'text-slate-400',
              )}
              title="Relative to parent"
            >
              {formattedRelPct}
            </span>
            <span className={cn('text-xs', isSelected && !isTerminal ? 'text-white/30' : 'text-slate-300')}>
              /
            </span>
            <span
              className={cn(
                'text-xs tabular-nums',
                isSelected && !isTerminal ? 'text-white/50' : 'text-slate-400/70',
              )}
              title="Relative to start"
            >
              {formattedAbsPct}
            </span>
          </span>
        ) : (
          <span
            className={cn(
              'text-sm font-semibold tabular-nums',
              isSelected && !isTerminal ? 'text-white/70' : 'text-slate-400',
            )}
          >
            {formattedRelPct}
          </span>
        )}
      </div>

      {/* Percentage bar */}
      {!isTerminal && (
        <div
          className={cn(
            'h-1 rounded-full overflow-hidden',
            isSelected ? 'bg-white/20' : 'bg-slate-100',
          )}
        >
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              isSelected ? 'bg-white/50' : isSessionTinted ? 'bg-slate-300' : 'bg-slate-300',
            )}
            style={{
              width: `${barWidth}%`,
              ...(!isSelected && !isSessionTinted ? { backgroundColor: `${accentColor}40` } : {}),
            }}
          />
        </div>
      )}

      {/* ── Timing row ────────────────────────────────────────────── */}
      {showTime && (hasStepTime || hasCumulativeTime) && (
        <div
          className={cn(
            'flex items-center gap-1.5 text-xs',
            isSelected && !isTerminal ? 'text-white/60' : 'text-slate-400',
          )}
        >
          <Clock className="w-3 h-3 flex-shrink-0" />
          {hasStepTime && (
            <span className="font-medium">+{formatDuration(avgTimeSec!)}</span>
          )}
          {hasStepTime && hasCumulativeTime && (
            <span className="opacity-40">|</span>
          )}
          {hasCumulativeTime && (
            <span className={cn(hasStepTime ? 'opacity-60' : 'font-medium')}>
              {isContinuationStart ? 'total ' : ''}Σ {formatDuration(cumulativeTimeSec!)}
            </span>
          )}
        </div>
      )}

      {/* ── KPI section ───────────────────────────────────────────── */}
      {hasAnyKpi && (
        <div
          className={cn(
            'flex flex-col gap-1 pt-1.5 mt-0.5',
            isSelected && !isTerminal ? 'border-t border-white/20' : 'border-t border-slate-100',
          )}
        >
          {kpiPct != null && kpiCount != null && (
            <div
              className={cn(
                'flex items-center gap-1.5 text-xs',
                isSelected && !isTerminal ? 'text-amber-200' : 'text-amber-600',
              )}
            >
              <TrendingUp className="w-3 h-3 flex-shrink-0" />
              <span className="font-bold tabular-nums">{kpiCount.toLocaleString()}</span>
              <span className="tabular-nums">({kpiPct.toFixed(1)}%)</span>
              <span className="opacity-50 text-[10px] ml-auto">session</span>
            </div>
          )}
          {crossSessionKpiPct != null && crossSessionKpiCount != null && (
            <div
              className={cn(
                'flex items-center gap-1.5 text-xs',
                isSelected && !isTerminal ? 'text-emerald-200' : 'text-emerald-600',
              )}
            >
              <Globe className="w-3 h-3 flex-shrink-0" />
              <span className="font-bold tabular-nums">{crossSessionKpiCount.toLocaleString()}</span>
              <span className="tabular-nums">({crossSessionKpiPct.toFixed(1)}%)</span>
              <span className="opacity-50 text-[10px] ml-auto">lifetime</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const FlowNodeMemo = memo(FlowNodeComponent);
